import { format, isDate } from 'date-fns'
import { jwtDecode } from 'jwt-decode'
import { ToastOptions, toast } from 'react-toastify'
import { getSecureItem, removeSecureItem, setSecureItem } from './secureStorage'

export const phoneRegExp = /^[\d\s\-()]+$/
export const timeRegex = /^([0-1]?\d|2[0-3]):[0-5]\d$/

// --- token storage ---
// BE issues two tokens; access is short-lived and Bearer-attached, refresh is long-lived
// and exchanged at /auth/refresh. Both live in localStorage AES-encrypted via
// REACT_APP_STORAGE_SECRET — callers see plaintext, the browser sees ciphertext.
const ACCESS_KEY = 'pcrm.accessToken'
const REFRESH_KEY = 'pcrm.refreshToken'

export const saveTokens = (accessToken: string, refreshToken: string) => {
  setSecureItem(ACCESS_KEY, accessToken)
  setSecureItem(REFRESH_KEY, refreshToken)
}

export const getAccessToken = (): string | null => getSecureItem(ACCESS_KEY)

export const getRefreshToken = (): string | null => getSecureItem(REFRESH_KEY)

export const clearTokens = () => {
  removeSecureItem(ACCESS_KEY)
  removeSecureItem(REFRESH_KEY)
}

// Back-compat shims for the older callers.
export const saveToken = (token: string) => saveTokens(token, '')
export const getToken = (): string | null => getAccessToken()
export const removeToken = () => clearTokens()

export const decodeToken = (token: string): Record<string, any> | null => {
  try {
    return jwtDecode(token)
  } catch (error) {
    return null
  }
}

export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true
  const currentTime = Date.now() / 1000
  return decoded.exp < currentTime
}

export const notifySuccess = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) =>
  toast.success(message, {
    ...options,
    containerId: options?.containerId ?? 'default',
    autoClose: 3000
  })

export const notifyInfo = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) =>
  toast.info(message, {
    ...options,
    containerId: options?.containerId ?? 'default',
    autoClose: 3000
  })

export const notifyWarning = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) =>
  toast.warning(message, {
    ...options,
    containerId: options?.containerId ?? 'default',
    autoClose: 3000
  })

export const notifyError = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) =>
  toast.error(message, {
    ...options,
    containerId: options?.containerId ?? 'default',
    autoClose: 3000
  })

export const toDropdownOptions = <T, V = string>({
  data,
  valueKey,
  labelKey
}: {
  data: T[],
  valueKey?: keyof T,
  labelKey?: keyof T
}): { value: V, label: string }[] => {
  return data.map((item) => {
    const value: V = valueKey
      ? (item[valueKey] as unknown as V)
      : (String(item) as V)
    const label = labelKey ? String(item[labelKey]) : String(item)
    return {
      value,
      label
    }
  })
}

export const formatDate = (date: string | null) => {
  if (!date) return ''

  try {
    return format(new Date(date), 'dd/MM/yyyy')
  } catch {
    return ''
  }
}

export const formatDateAndTime = (date: string | null) => {
  if (!date) return ''

  try {
    // Server returns naive timestamps without a timezone marker; the historical
    // contract here is "treat as UTC". For inputs that already carry a marker
    // (e.g. `2026-05-26T18:18:00.000Z` from `Date.toISOString()`, or anything
    // with `+05:30`), don't double-append — that produces an Invalid Date and
    // the row renders blank.
    const hasTz = /Z|[+-]\d\d:?\d\d$/.test(date)
    const normalized = hasTz ? date : `${date}Z`
    return format(new Date(normalized), 'dd/MM/yyyy HH:mm')
  } catch {
    return ''
  }
}

/**
 * Formats a date, including the time when the value carries one.
 * - Date-only strings (e.g. `2026-05-14`) → `14/05/2026`
 * - ISO datetime strings (e.g. `2026-05-15T10:18:11Z`) → `15/05/2026 10:18`
 */
export const formatDateMaybeTime = (date: string | null | undefined) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    const hasTime = typeof date === 'string' && date.includes('T')
    return format(d, hasTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
  } catch {
    return ''
  }
}
export const formatDateRange = (
  from: string | Date | null,
  to: string | Date | null
) => {
  let fromDate: Date | undefined
  let toDate: Date | undefined
  if (from) {
    fromDate = isDate(from) ? (from as Date) : new Date(from)
    if (!to) return `After ${fromDate.toDateString()}`
  }

  if (to) {
    toDate = isDate(to) ? (to as Date) : new Date(to)
    if (!from) return `Before ${toDate.toDateString()}`
  }

  if (!fromDate || !toDate) return 'All Dates'

  if (
    fromDate.toString() === 'Invalid Date' ||
    toDate.toString() === 'Invalid Date'
  ) {
    return ''
  }

  const options = {
    year: 'numeric' as const,
    month: '2-digit' as const,
    day: '2-digit' as const
  }

  const dateTimeFormat = new Intl.DateTimeFormat('en-GB', options)
  return dateTimeFormat.formatRange(fromDate, toDate)
}

export type DateTypes = Date | string | null | undefined

export const isValidDate = (dateObject: DateTypes): boolean => {
  if (!dateObject) return false
  return new Date(dateObject).toString() !== 'Invalid Date'
}

export const isValidDateRange = (from: DateTypes, to: DateTypes): boolean => {
  if (!from || !to) return false
  if (!isValidDate(from) || !isValidDate(to)) return false
  const fromDate = isDate(from) ? (from as Date) : new Date(from)
  const toDate = isDate(to) ? (to as Date) : new Date(to)

  if (toDate < fromDate) return false
  return true
}

export const toServerDateFormat = (date?: Date) => {
  return date ? format(date, 'yyyy-MM-dd') : undefined
}

export const toLowerCaseByKey =
  <T extends object>(key: keyof T) =>
  (object: T) =>
    (object[key] as string).toLowerCase()

export const getRandomString = (length: number): string => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const charactersLength = characters.length
  for (let i = 0; i < length; i += 1) {
    const { crypto } = window
    const array = new Uint32Array(1)
    const randomNum = crypto.getRandomValues(array)[0]
    const randomIndex = randomNum % charactersLength
    result += characters.charAt(randomIndex)
  }
  return result
}

export const getAPIValidationErrorMessage = (
  errorResponse?: Array<Record<string, Array<Record<string, any>>>> | null
): string => {
  let errorMsg = ''
  if (errorResponse) {
    const errorBody = errorResponse?.length > 0 ? errorResponse[0] : {}
    const errorKeys = Object.keys(errorBody)
    if (errorKeys.length) {
      errorMsg = `${errorKeys[0]} : ${errorBody[errorKeys[0]][0]?.errorDescription}`
    }
  }
  return errorMsg
}

interface FormValues {
  [key: string]: any;
}

export const getChangedValues = (
  originalObj: FormValues,
  updatedObj: FormValues
): Partial<FormValues> => {
  const changes: Partial<FormValues> = {}

  for (const key in originalObj) {
    if (Object.prototype.hasOwnProperty.call(originalObj, key)) {
      if (
        originalObj[key as keyof FormValues] !==
        updatedObj[key as keyof FormValues]
      ) {
        changes[key as keyof FormValues] = updatedObj[key as keyof FormValues]
      }
    }
  }

  return changes
}

export const getModalTitleText = (isLoading: boolean, exp?: any): string => {
  if (!isLoading) {
    return exp || ''
  }
  return ''
}

export const getDashboardPath = (): string => '/dashboard'
